import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";
import DatePicker from "react-multi-date-picker";
import ClipLoader from "react-spinners/ClipLoader";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import { Dialog, Button, Dropdown, TextInput, Image, WarningPopUp } from "hesus-ui-kit";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import UserInfo from "@utils/UserInfo";
import { renderPriceWithCurrency } from "@utils/utils";
import closeIcon from "@icons/ic-close-white.svg";
import commentIcon from "@icons/ic-comment-blue.svg";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import PurchaseOrderSupplierSelect from "./components/PurchaseOrderSupplierSelect/PurchaseOrderSupplierSelect";
import PurchaseOrderItemSelector from "./components/PurchaseOrderItemSelector/PurchaseOrderItemSelector";
import PurchaseOrderLineItems from "./components/PurchaseOrderLineItems/PurchaseOrderLineItems";
import PurchaseOrderContacts from "./components/PurchaseOrderContacts/PurchaseOrderContacts";
import {
  HeaderWrapper,
  Title,
  FormBody,
  OptionsWrapper,
  ButtonWrapper,
  ButtonWrapperFooter,
  SectionContainer,
  FlexRow,
  GridRow,
  SectionTitle,
  ContainerLabel,
  CommentLink,
  ApiErrorText,
  ErrorText,
  GenericErrorBanner,
} from "./AddEditPurchaseOrder.styled";

const DROPDOWN_ERROR_STYLES = {
  position: "absolute",
  top: "100%",
  left: "0",
  marginTop: "2px",
};

let lineItemIdCounter = 0;
const generateLineItemId = () => {
  lineItemIdCounter += 1;
  return `po-line-${Date.now()}-${lineItemIdCounter}`;
};

const AddEditPurchaseOrder = ({
  t,
  isEditMode: isEditModeProp,
  id: idProp,
  context,
  siteId,
  siteName,
  siteOpportunityNumber,
  companyId,
  companyName,
  platformCountryId,
  onClose,
  updateRefreshStatus,
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const isEditMode = isEditModeProp ?? !!params?.id;
  const id = idProp ?? params?.id;
  const effectiveContext = context ?? (siteId ? "site" : "company");
  const handleClose = onClose ?? (() => navigate(-1));
  const currentUser = UserInfo.getUser();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [descriptionData, setDescriptionData] = useState("");
  const [lineItems, setLineItems] = useState([]);
  const [lineItemErrors, setLineItemErrors] = useState({});
  const [contacts, setContacts] = useState([]);
  const [contactsError, setContactsError] = useState("");
  const [reference, setReference] = useState("");
  const [poStatus, setPoStatus] = useState(0);
  const [selectedSite, setSelectedSite] = useState(null);
  const effectiveSiteId =
    effectiveContext === "site" ? siteId : selectedSite?.id;
  const [isCancelConfirmVisible, setCancelConfirmVisible] = useState(false);

  const [hesusContactOptions, setHesusContactOptions] = useState([]);
  const [paymentTermOptions, setPaymentTermOptions] = useState([]);
  const [loadingHesusContacts, setLoadingHesusContacts] = useState(false);
  const [loadingPaymentTerms, setLoadingPaymentTerms] = useState(false);
  const [loadingPO, setLoadingPO] = useState(false);

  const initialSnapshotRef = useRef(null);

  const [formData, setFormData] = useState({
    supplier: {
      required: true,
      value:
        effectiveContext === "company" && companyId && !isEditModeProp
          ? { id: companyId, name: companyName }
          : "",
      error: "",
    },
    hesusContact: { required: true, value: "", error: "" },
    paymentTerm: { required: true, value: "", error: "" },
    orderDate: {
      required: true,
      value: isEditMode ? "" : moment().format("YYYY-MM-DD"),
      error: "",
    },
    supplierQuoteNumber: { required: false, value: "", error: "" },
  });

  const fetchHesusContacts = (countryId) => {
    if (!countryId) return;
    setLoadingHesusContacts(true);
    axios
      .get(`/general/search?countries=[${countryId}]&limit=50&objectTypes=["admin"]`)
      .then((response) => {
        const admins = response.data?.admins || response.data || [];
        setHesusContactOptions(
          admins.map((a) => ({
            value: a.id,
            label: `${a.firstName || ""} ${a.lastName || ""}`.trim(),
          }))
        );
      })
      .catch(() => setHesusContactOptions([]))
      .finally(() => setLoadingHesusContacts(false));
  };

  const fetchPaymentTerms = (countryId) => {
    if (!countryId) return;
    setLoadingPaymentTerms(true);
    axios
      .get(`/general/paymentTerms?countries=[${countryId}]&limit=-1`)
      .then((response) => {
        const terms = response.data?.paymentTerm || [];
        
        setPaymentTermOptions(
          terms.map((pt) => ({ value: pt.id, label: pt.label }))
        );
      })
      .catch(() => setPaymentTermOptions([]))
      .finally(() => setLoadingPaymentTerms(false));
  };

  const mapPurchaseOrderToState = (po) => {
    setReference(po.reference ?? "");
    setPoStatus(po.status ?? 0);
    setFormData({
      supplier: { required: true, value: po.supplier ?? "", error: "" },
      hesusContact: {
        required: true,
        value: po.hesusContact
          ? {
              value: po.hesusContact.id,
              label: `${
                po.hesusContact.firstname || po.hesusContact.firstName || ""
              } ${
                po.hesusContact.lastname || po.hesusContact.lastName || ""
              }`.trim(),
            }
          : "",
        error: "",
      },
      paymentTerm: {
        required: true,
        value: po.paymentTerm
          ? { value: po.paymentTerm.id, label: po.paymentTerm.label }
          : "",
        error: "",
      },
      orderDate: {
        required: true,
        value: po.orderDate
          ? moment(po.orderDate, ["YYYY-MM-DD", "DD-MM-YYYY"]).format(
              "YYYY-MM-DD"
            )
          : "",
        error: "",
      },
      supplierQuoteNumber: {
        required: false,
        value: po.supplierQuoteNumber ?? "",
        error: "",
      },
    });
    setContacts(
      (po.contacts || []).map((c) => ({
        ...c,
        id: c.contactId,
        purchaseOrderContactId: c.id,
      }))
    );
    setDescriptionData(po.comment ?? "");
    setIsCommentOpen(!!po.comment);
    setLineItems(
      (po.items || []).map((item) => {
        const jobsite = item.jobsite;
        const quoteItem = item.quoteItem;
        const isFree = !quoteItem;
        return {
          id: generateLineItemId(),
          purchaseOrderLineItemId: item.id ?? null,
          groupKey: isFree ? null : `${jobsite?.id}_${item.quoteService?.id}`,
          groupLabel: isFree
            ? ""
            : `#${jobsite?.opportunityNumber ?? jobsite?.id ?? ""} - ${
                jobsite?.name ?? ""
              } / ${item.quoteService?.label ?? ""}`,
          hesusServiceName: quoteItem?.hesusService?.name ?? "",
          hesusServicePriceLabel: quoteItem
            ? `(${(quoteItem.itemSellingPrice ?? quoteItem.sellingPrice ?? quoteItem.unitPrice ?? 0).toFixed(2)} ${item.quoteService?.currency?.symbol ?? quoteItem.currencySymbol ?? "€"}/${t(quoteItem.unit) || t(item.unit) || ""})`
            : "",
          label: item.label ?? "",
          product: item.product ?? "",
          truckType: item.transportType ?? "",
          quantity: item.quantity ?? "",
          unitPrice: item.unitPrice ?? "",
          unit: item.unit ?? "",
          comment: item.comment ?? "",
          isCommentOpen: !!item.comment,
          jobsiteId: jobsite?.id ?? null,
          quoteItemId: quoteItem?.id ?? null,
        };
      })
    );
  };

  const fetchPurchaseOrder = () => {
    setLoadingPO(true);
    axios
      .get(`/purchaseOrders/${id}`)
      .then((response) => {
        mapPurchaseOrderToState(response.data?.purchaseOrders?.[0]);
      })
      .catch((error) => {
        setApiError(
          error.response?.data?.message ?? "Failed to load purchase order"
        );
      })
      .finally(() => setLoadingPO(false));
  };

  useEffect(() => {
    fetchHesusContacts(platformCountryId);
    fetchPaymentTerms(platformCountryId);

    if (currentUser && !isEditMode) {
      setFormData((prev) => ({
        ...prev,
        hesusContact: {
          ...prev.hesusContact,
          value: {
            value: currentUser.id,
            label: `${currentUser.firstName || ""} ${
              currentUser.lastName || ""
            }`.trim(),
          },
        },
      }));
    }

    if (isEditMode && id) {
      fetchPurchaseOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialSnapshotRef.current === null && !loadingPO) {
      initialSnapshotRef.current = JSON.stringify({
        supplier: formData.supplier.value,
        hesusContact: formData.hesusContact.value,
        paymentTerm: formData.paymentTerm.value,
        orderDate: formData.orderDate.value,
        supplierQuoteNumber: formData.supplierQuoteNumber.value,
        contacts,
        comment: descriptionData,
        items: lineItems,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, contacts, formData, descriptionData, loadingPO]);

  const onFieldChange = (field, data) => {
    setFormData({
      ...formData,
      [field]: { ...formData[field], value: data.value, error: "" },
    });


  };

  const getSupplierCountryId = (supplier) => {
    if (!supplier) return null;
    return (
      supplier?.country?.id ??
      supplier?.countryId ??
      supplier?.billingAddress?.country ??
      supplier?.platformCountry?.id ??
      null
    );
  };

  const onSupplierChange = (event, newValue) => {
    const currentSupplierId = formData.supplier.value?.id;
    const newSupplierId = newValue?.id;

    setFormData({
      ...formData,
      supplier: { required: true, value: newValue, error: "" },
    });

    if (!newSupplierId || newSupplierId === currentSupplierId) {
      return;
    }

    setContacts([]);
    const supplierCountryId = getSupplierCountryId(newValue);
    if (supplierCountryId) {
      fetchHesusContacts(supplierCountryId);
      fetchPaymentTerms(supplierCountryId);
    }
  };

  const onAddLine = ({ quoteItem, quoteService, siteId: currentSiteId, siteName: currentSiteName, siteOpportunityNumber: currentOpportunityNumber }) => {
    const newItem = {
      id: generateLineItemId(),
      groupKey: `${currentSiteId}_${quoteService.id}`,
      groupLabel: `#${currentOpportunityNumber ?? currentSiteId} - ${
        currentSiteName || ""
      } / ${quoteService.label}`,
      hesusServiceName: quoteItem.hesusService.name,
      hesusServicePriceLabel: `(${quoteItem.sellingPrice.toFixed(2)} ${
        quoteItem.currencySymbol || "€"
      }/${quoteItem.unit})`,
      hesusServicePriceLabel: renderPriceWithCurrency(`${quoteItem.sellingPrice.toFixed(2)}`, t(quoteItem.unit), quoteService.currency.symbol, quoteService.currency.position),
      label: quoteItem.label,
      product: quoteItem.product?.name ?? "",
      truckType: quoteItem.transport?.name ?? "",
      quantity: quoteItem.quantity ?? "",
      unitPrice: "",
      unit: quoteItem.unit ?? "",
      comment: "",
      isCommentOpen: false,
      jobsiteId: currentSiteId,
      quoteItemId: quoteItem.id,
    };
    setLineItems((prev) => [...prev, newItem]);
  };

  const onAddFreeLine = ({
    siteId: currentSiteId,
    siteName: currentSiteName,
    siteOpportunityNumber: currentOpportunityNumber,
  }) => {
    const newItem = {
      id: generateLineItemId(),
      groupKey: currentSiteId ? `${currentSiteId}_free` : null,
      groupLabel: currentSiteId
        ? `#${currentOpportunityNumber ?? currentSiteId} - ${
            currentSiteName || ""
          } / Free line`
        : "",
      hesusServiceName: "",
      hesusServicePriceLabel: "",
      label: "",
      product: "",
      truckType: "",
      quantity: "",
      unitPrice: "",
      unit: "",
      comment: "",
      isCommentOpen: false,
      jobsiteId: currentSiteId,
    };
    setLineItems((prev) => [...prev, newItem]);
  };

  const validateForm = () => {
    setHasSubmitted(true);
  
    const data = { ...formData };
    let isFormValid = true;
    console.log('FORM data', JSON.stringify(data, null, 2));

    if (!data.supplier.value) {
      isFormValid = false;
      data.supplier.error = t("FIELD_REQUIRED");
    }
    if (!data.hesusContact.value) {
      isFormValid = false;
      data.hesusContact.error = t("FIELD_REQUIRED");
    }
    if (!data.paymentTerm.value) {
      isFormValid = false;
      data.paymentTerm.error = t("FIELD_REQUIRED");
    }
    if (!data.orderDate.value) {
      isFormValid = false;
      data.orderDate.error = t("FIELD_REQUIRED");
    }

    if (contacts.length === 0) {
      isFormValid = false;
      setContactsError(t("FIELD_REQUIRED"));
    } else {
      setContactsError("");
    }

    if (effectiveContext === "site" && !effectiveSiteId) {
      isFormValid = false;
    }

    const itemErrors = {};
    if (lineItems.length === 0) {
      itemErrors.lineItems = t("FIELD_REQUIRED");
      isFormValid = false;
    } else {
      const hasInvalidLine = lineItems.some(
        (item) => !item.label || !item.quantity || !item.unitPrice || !item.unit
      );
      if (hasInvalidLine) {
        itemErrors.lineItems = t("FIELD_REQUIRED");
        isFormValid = false;
      }
    }
    setLineItemErrors(itemErrors);

    setFormData({ ...data });
    console.log('FORM DATA ERROR', isFormValid);
    return isFormValid;
  };

  const getFormData = () => ({
    id: isEditMode ? id : "",
    site:
      effectiveContext === "site"
        ? { id: siteId }
        : selectedSite
        ? { id: selectedSite.id }
        : null,
    supplier: formData.supplier.value
      ? { id: formData.supplier.value.id }
      : null,
    country: {
      id: getSupplierCountryId(formData.supplier.value) ?? platformCountryId,
    },
    contacts: contacts.map((c) => ({
      id: c.purchaseOrderContactId ?? "",
      contactId: c.id,
      relatedContactId: c.relatedContactId??"",
    })),
    hesusContact: formData.hesusContact.value
      ? { id: formData.hesusContact.value.value }
      : null,
    paymentTerm: formData.paymentTerm.value
      ? { id: formData.paymentTerm.value.value }
      : null,
    orderDate: formData.orderDate.value
      ? moment(formData.orderDate.value).format("YYYY-MM-DD")
      : "",
    supplierQuoteNumber: formData.supplierQuoteNumber.value,
    comment: descriptionData,
    items: lineItems.map((item, index) => ({
      id: item.purchaseOrderLineItemId ?? "",
      label: item.label,
      product: item.product,
      transportType: item.truckType,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      totalPrice: (item.quantity || 0) * (item.unitPrice || 0),
      unit: item.unit,
      comment: item.comment,
      sortOrder: index,
      ...(item.jobsiteId ? { jobsite: { id: item.jobsiteId } } : {}),
      ...(item.quoteItemId ? { quoteItem: { id: item.quoteItemId } } : {}),
    })),
    totalAmountExclTax: lineItems.reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0),
      0
    ),
    createdBy: currentUser?.id,
    updatedBy: currentUser?.id,
  });

  const submitForm = () => {
    setIsSubmitting(true);
    setApiError("");

    const request = !isEditMode
      ? axios.post(`/purchaseOrders`, getFormData())
      : axios.put(`/purchaseOrders/${id}`, getFormData());

    request
      .then(() => {
        setIsSubmitting(false);
        if (updateRefreshStatus) updateRefreshStatus();
        handleClose();
      })
      .catch((error) => {
        setIsSubmitting(false);
        setApiError(
          error.response?.data?.message ?? "Failed to save purchase order"
        );
      });
  };

  const onSubmit = () => {
    if (!validateForm()) return;
    submitForm();
  };

  const isDirty = () => {
    if (!initialSnapshotRef.current) return true;
    const currentSnapshot = JSON.stringify({
      supplier: formData.supplier.value,
      hesusContact: formData.hesusContact.value,
      paymentTerm: formData.paymentTerm.value,
      orderDate: formData.orderDate.value,
      supplierQuoteNumber: formData.supplierQuoteNumber.value,
      contacts,
      comment: descriptionData,
      items: lineItems,
    });
    return currentSnapshot !== initialSnapshotRef.current;
  };

  const onCancelClick = () => {
    handleClose();
  };

  const onPopUpCancel = () => {
    setCancelConfirmVisible(false);
  };

  const onPopUpConfirm = () => {
    setCancelConfirmVisible(false);
    handleClose();
  };

  const getCreateTitle = () => {
    const base = t("PURCHASE_ORDER_FORM_TITLE_NEW");
    const contextName =
      effectiveContext === "site" && siteName
        ? siteName
        : effectiveContext === "company" && companyName
        ? companyName
        : "";
    return contextName ? `${base} — ${contextName}` : base;
  };

  const title = isEditMode
    ? `${t("PURCHASE_ORDER_FORM_TITLE_EDIT")} — ${reference}`
    : getCreateTitle();

  return (
    <Dialog width="65rem">
      <div>
        <HeaderWrapper>
          <Title>{title}</Title>
          <Button
            onClick={handleClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image src={closeIcon} alt="close purchase order popup" />
          </Button>
        </HeaderWrapper>
        <FormBody $isLoading={isEditMode && loadingPO}>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <SectionTitle>{t("PURCHASE_ORDER_INFORMATION")}</SectionTitle>

                <GridRow column="1fr 1fr">
                  <PurchaseOrderSupplierSelect
                    t={t}
                    value={formData.supplier.value}
                    onChange={onSupplierChange}
                    countryId={platformCountryId}
                    companyId={companyId}
                    companyName={companyName}
                    error={formData.supplier.error}
                    disabled={false}
                  />
                  <PurchaseOrderContacts
                    contacts={contacts}
                    setContacts={setContacts}
                    supplierId={formData.supplier.value?.id}
                    error={contactsError}
                  />
                </GridRow>
                <GridRow
                  column="1fr 1fr 1fr 1fr"
                  style={{
                    alignItems: "end",
                    marginTop: "16px",
                    marginBottom: "30px",
                  }}
                >
                  <Dropdown
                    id="po-hesus-contact"
                    label={t("PURCHASE_ORDER_HESUS_CONTACT")}
                    name="po-hesus-contact"
                    value={formData.hesusContact.value?.value || ""}
                    onChange={(e) => {
                      const selected = hesusContactOptions.find(
                        (o) => o.value === e.value
                      );
                      onFieldChange("hesusContact", {
                        value: selected
                          ? { value: selected.value, label: selected.label }
                          : "",
                      });
                    }}
                    defaultOption={t("PURCHASE_ORDER_HESUS_CONTACT")}
                    options={hesusContactOptions}
                    required
                    disabled={loadingHesusContacts}
                    labelStyles={
                      formData.hesusContact.error !== ""
                        ? labelStylesError
                        : labelStyles
                    }
                    inputStyles={
                      formData.hesusContact.error !== ""
                        ? inputStylesError
                        : inputStyles
                    }
                    error={formData.hesusContact.error}
                    errorStyles={DROPDOWN_ERROR_STYLES}
                  />

                  <Dropdown
                    id="po-payment-term"
                    label={t("PURCHASE_ORDER_PAYMENT_TERMS")}
                    name="po-payment-term"
                    value={formData.paymentTerm.value?.value || ""}
                    onChange={(e) => {
                      const selected = paymentTermOptions.find(
                        (o) => o.value === e.value
                      );
                      onFieldChange("paymentTerm", {
                        value: selected
                          ? { value: selected.value, label: selected.label }
                          : "",
                      });
                    }}
                    defaultOption={t("PURCHASE_ORDER_PAYMENT_TERMS")}
                    options={paymentTermOptions}
                    required
                    disabled={loadingPaymentTerms}
                    labelStyles={
                      formData.paymentTerm.error !== ""
                        ? labelStylesError
                        : labelStyles
                    }
                    inputStyles={
                      formData.paymentTerm.error !== ""
                        ? inputStylesError
                        : inputStyles
                    }
                    error={formData.paymentTerm.error}
                    errorStyles={DROPDOWN_ERROR_STYLES}
                  />

                  <div>
                    <ContainerLabel required htmlFor="po-order-date">
                      {t("PURCHASE_ORDER_ORDER_DATE")}
                    </ContainerLabel>
                    <DatePicker
                      id="po-order-date"
                      name="po-order-date"
                      format={getDatePickerFormat()}
                      locale={getCalendarLocale()}
                      value={
                        getDateFormat(formData.orderDate.value, "YYYY-MM-DD") ||
                        ""
                      }
                      onChange={(e) =>
                        onFieldChange("orderDate", {
                          value: e
                            ? moment(e.toDate()).format("YYYY-MM-DD")
                            : "",
                        })
                      }
                      calendarPosition="bottom-center"
                      style={{
                        padding: "5px 10px",
                        backgroundColor: "white",
                        border: formData.orderDate.error
                          ? "1px solid red"
                          : "1px solid #474747",
                        borderRadius: "4px",
                        fontFamily: "Lato",
                        fontSize: "16px",
                        height: "40px",
                        width: "100%",
                        lineHeight: "1.4",
                        boxSizing: "border-box",
                      }}
                    />
                    {formData.orderDate.error && (
                      <ErrorText style={{ display: "block" }}>
                        {formData.orderDate.error}
                      </ErrorText>
                    )}
                  </div>

                  <TextInput
                    type="text"
                    label={t("PURCHASE_ORDER_QUOTE_NUMBER")}
                    onChange={(e) =>
                      onFieldChange("supplierQuoteNumber", {
                        value: e.target.value,
                      })
                    }
                    name="po-supplier-quote-number"
                    id="po-supplier-quote-number"
                    value={String(formData.supplierQuoteNumber.value) || ""}
                    isFloat
                    labelStyles={{ ...labelStyles, top: "0px" }}
                    containerStyles={{ marginTop: "21px" }}
                    inputStyles={{
                      ...inputStyles,
                      lineHeight: "1.5",
                      border: "1px solid #767676",
                    }}
                    error={formData.supplierQuoteNumber.error}
                  />
                </GridRow>

                {!isCommentOpen && (
                  <CommentLink onClick={() => setIsCommentOpen(true)}>
                    <Image src={commentIcon} alt="add comment" />
                    {t("PURCHASE_ORDER_ADD_COMMENT")}
                  </CommentLink>
                )}
                {isCommentOpen && (
                  <div style={{ marginBottom: "20px" }}>
                    <ContainerLabel>
                      {t("PURCHASE_ORDER_COMMENT")}
                    </ContainerLabel>
                    <CKEditorText
                      descriptionData={descriptionData}
                      setDescriptionData={setDescriptionData}
                      maxHeight="80px"
                    />
                  </div>
                )}

                <SectionTitle>{t("PURCHASE_ORDER_ITEMS")}</SectionTitle>

                <PurchaseOrderItemSelector
                  context={effectiveContext}
                  supplierId={formData.supplier.value?.id}
                  siteId={siteId}
                  siteName={siteName}
                  siteOpportunityNumber={siteOpportunityNumber}
                  searchCountryId={
                    getSupplierCountryId(formData.supplier.value) ??
                    platformCountryId
                  }
                  selectedSite={selectedSite}
                  setSelectedSite={setSelectedSite}
                  onAddLine={onAddLine}
                  onAddFreeLine={onAddFreeLine}
                />

                <PurchaseOrderLineItems
                  lineItems={lineItems}
                  setLineItems={setLineItems}
                  errors={lineItemErrors}
                  status={poStatus}
                />
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>
        <ButtonWrapperFooter>
          {apiError && <ApiErrorText>{apiError}</ApiErrorText>}
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={() =>
              isDirty() ? setCancelConfirmVisible(true) : onCancelClick()
            }
          >
            <>{t("GENERIC_ACTION_CANCEL")}</>
          </Button>
          <Button
            disabled={isEditMode && loadingPO}
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isSubmitting && <ClipLoader size={15} color="#fff" />}
              {!isSubmitting && t("GENERIC_ACTION_VALIDATE")}
            </>
          </Button>
        </ButtonWrapperFooter>
      </div>
      {isCancelConfirmVisible && (
        <WarningPopUp
          title={t("PURCHASE_ORDER_CANCEL_CONFIRMATION_TITLE")}
          message={t("PURCHASE_ORDER_CANCEL_CONFIRMATION_MESSAGE")}
          onPopUpCancel={onPopUpCancel}
          onPopUpConfirm={onPopUpConfirm}
          ValidateContent={t("YES")}
          CancelContent={t("GENERIC_ACTION_CANCEL")}
        />
      )}
    </Dialog>
  );
};

AddEditPurchaseOrder.propTypes = {
  t: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  id: PropTypes.number,
  context: PropTypes.oneOf(["site", "company"]),
  siteId: PropTypes.number,
  siteName: PropTypes.string,
  siteOpportunityNumber: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  companyId: PropTypes.number,
  companyName: PropTypes.string,
  platformCountryId: PropTypes.number.isRequired,
  onClose: PropTypes.func,
  updateRefreshStatus: PropTypes.func,
};

AddEditPurchaseOrder.defaultProps = {
  isEditMode: false,
  id: null,
  context: 'company',
  siteId: null,
  siteName: "",
  siteOpportunityNumber: null,
  companyId: null,
  companyName: "",
  updateRefreshStatus: null,
  platformCountryId: 2,
};

export default withTranslation()(AddEditPurchaseOrder);
